/**
 * PhpUserMgmtPage — proper React-native user management page for the
 * legacy PHP `user.user` + `user.UserOperation` records.
 *
 * Replaces the 1:1 PHP-spec mirror (which rendered 37 narrow columns
 * interleaved with tool-columns, no pinned identity, and — because the
 * overlay that appends user.UserOperation tool-cols tripped the generic
 * renderer's `hasOperat` check — lost the native 编辑 / 删除 buttons on
 * /parity/user_user entirely). This page re-organises the same data so
 * operators can:
 *
 *   · find a user fast (pinned left:  ID / 账号 / 编号 / 昵称)
 *   · read the key balances and levels at a glance (middle, scrollable)
 *   · fire every admin action from one pinned-right 操作 column:
 *       详情 · 编辑 · 删除 · 重置登录密码 · 重置交易密码 · 重置密保
 *       · 冻结/解冻 · EP 限额 · 红心
 *
 * Data source: `php_user_user` mirror table — same records as PHP
 * `user.user/index` and `user.UserOperation/index`; those two PHP pages
 * are just different column layouts over the same table. We therefore
 * mount this component for both `/parity/user_user` (emphasises balances)
 * and `/parity/user_useroperation` (emphasises admin actions); the diff
 * is driven by the `variant` prop.
 *
 * Writes:
 *   · 编辑 (nickname / 红心 blood / 激活 is_activation) → PATCH mirror
 *   · 删除 → DELETE mirror row
 *   · 重置登录密码 / 重置密保 → 弹窗手动输入后 POST /php-action
 *   · 重置交易密码 → POST /php-action (instant confirm)
 *   · 冻结 / EP 限额 / 红心 → POST /php-action with form payload
 * Every write refetches the list so the row reflects the new state.
 *
 * On l / r (区业绩):
 *   These are legacy PHP `l` / `r` mirror fields. They are **not** the same
 *   thing as「主账户 vs 子账户」(-001 / -002), and they are **not** a
 *   reliable label for「网体双轨下线的左右区」— that was an earlier
 *   misread. The identity columns (account / account_kind) cover master vs sub.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Drawer,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popover,
  Row,
  Segmented,
  Select,
  Space,
  Spin,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { DownloadOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { api } from '../../api/client';
import { confirmAndDelete } from './PhpNativeForm';
import { useAuthStore } from '../../store/auth';
import { ShiftUplineForm } from '../../components/ShiftUplineModal';
import { DeleteUserForm } from '../../components/DeleteUserModal';

const TABLE = 'php_user_user';
/** Server-side grouped endpoint (packages/backend/src/modules/parity/parity.routes.ts).
 * Classifies every php_user_user row as master/sub/bot by inspecting the
 * `account` column, so the frontend can default to showing 主账户 only and
 * lazy-expand their 子账户s via a second call with master_account=... */
const GROUPED_URL = '/api/admin/parity/user-mirror-grouped';

type AccountKind = 'master' | 'sub' | 'bot' | 'all';

const ACCOUNT_KIND_OPTIONS: { value: AccountKind; label: string }[] = [
  { value: 'master', label: '主账户' },
  { value: 'sub', label: '子账户' },
  { value: 'bot', label: '虚拟账户' },
  { value: 'all', label: '全部' },
];

const ACCOUNT_KIND_TAG: Record<string, { label: string; color: string }> = {
  master: { label: '主账户', color: 'blue' },
  sub: { label: '子账户', color: 'geekblue' },
  bot: { label: '虚拟账户', color: 'default' },
  unknown: { label: '未知', color: 'default' },
};

/* ------------------------ level dictionaries ---------------------------- */
/** Mirrors the selectLists in php-page-specs.generated.json. Duplicated
 * here so this page does not need to reload the ~7000-line spec file at
 * runtime. If the legacy PHP admin grows new levels the crawler will
 * still catch them in the raw `_raw.userLevel.title` field — we fall
 * back to that text when the numeric key is unknown. */
const USER_LEVEL_TITLE: Record<string, string> = { '1': '100$', '2': '1000$' };
const REWARD_LEVEL_TITLE: Record<string, string> = {
  '1': 'E1【一星】', '2': 'E2【二星】', '3': 'E3【三星】',
  '4': 'A1【水星】', '5': 'A2【火星】', '6': 'A3【金星】',
  '7': 'A4【土星】', '8': 'A5【木星】',
  '9': 'S1【地月系大使】', '10': 'S2【太阳系大使】', '11': 'S3【恒星系大使】',
  '12': 'S4【银河系大使】', '13': 'S5【麦哲伦星系大使】',
  '14': 'S6【仙女座星系大使】', '15': 'S7【本星系大使】',
  '16': 'S8【总星系大使】', '17': 'S9【宇宙大使】',
};
const DONATION_LEVEL_TITLE: Record<string, string> = {
  '0': '暂无',
  '1': 'A1【好心人】', '2': 'A2【爱心志愿者】', '3': 'A3【爱心达人】',
  '4': 'A4【公益明星】', '5': 'A5【公益大使】',
  '6': 'S1【青铜慈善家】', '7': 'S2【白银慈善家】', '8': 'S3【黄金慈善家】',
  '9': 'S4【铂金慈善家】', '10': 'S5【翡翠慈善家】', '11': 'S6【钻石慈善家】',
  '12': 'S7【星耀慈善家】', '13': 'S8【皇冠慈善家】',
  '14': 'SS1【慈善豪门】', '15': 'SS2【首席慈善豪门】',
  '16': 'SS3【王者慈善豪门】', '17': 'SS4【至尊慈善豪门】',
};
const IS_OUT_TITLE: Record<string, string> = { '0': '否', '1': '是' };
const IS_ACTIVATION_TITLE: Record<string, string> = { '0': '否', '1': '是' };

const LEVEL_TIME_OPTIONS = [
  { value: '0', label: '否' },
  { value: '1', label: '正序' },
  { value: '2', label: '倒序' },
];

const SUB_COUNT_OPTIONS = [
  { value: '-1', label: '无子账号' },
  { value: '-2', label: '1~10个' },
  { value: '-3', label: '11~20个' },
  { value: '-4', label: '21~30个' },
  { value: '-5', label: '31~40个' },
  { value: '-6', label: '41~50个' },
  { value: '-7', label: '51~60个' },
  { value: '-8', label: '61~70个' },
  { value: '-9', label: '71~80个' },
  { value: '-10', label: '81~90个' },
  { value: '-11', label: '91~100个' },
  { value: '-12', label: '101个及以上' },
];

interface AdvancedSearchValues {
  id?: string;
  account?: string;
  number?: string;
  nickname?: string;
  address?: string;
  user_level?: string;
  level_time?: string;
  is_out?: string;
  reward_level?: string;
  jihuo_num?: string;
  donate?: string;
  l?: string;
  r?: string;
  sub_count?: string;
  create_time?: string;
  freeze?: string;
  blood?: string;
  is_activation?: string;
}

/* -------------- URL paths for the PHP /php-action proxy ----------------- */
/** Exact `init.*_url` paths captured from `admin/js/user/user_operation.js`.
 * The Node backend proxies these verbatim to the legacy PHP controller
 * via POST /php-action, then re-pulls the row into the mirror. */
const PHP_ACTION = {
  /** 重置登录密码 — POST-only, no form fields. */
  resetLogin: 'user.user_operation/edit',
  /** 清空密保信息 — POST-only, no form fields. */
  clearSecret: 'user.user_operation/edit1',
  /** 重置交易密码 — POST-only, no form fields. */
  resetTrade: 'user.user_operation/edit2',
  /** 冻结/解冻账户 — form: freeze (date-offset select) + freeze_remark. */
  freeze: 'user.user_operation/edit3',
  /** 每日 EP1/EP2 限额 — form: days_ep_limit (number). */
  epLimit: 'user.user_operation/edit4',
  /** 红心操作 — form: blood (number). */
  blood: 'user.user_operation/edit5',
} as const;

/* ---------------------------- types ------------------------------------- */
interface UserRow extends Record<string, unknown> {
  id: string;
  account?: string;
  number?: string;
  nickname?: string;
  address?: string;
  blood?: string;
  uo?: string;
  uo_?: string;
  rp?: string;
  tp?: string;
  ep?: string;
  ep1?: string;
  ep2?: string;
  sp1?: string;
  sp2?: string;
  sp3?: string;
  mp?: string;
  ap?: string;
  fp?: string;
  donate?: string;
  team_sum?: string;
  my_sum?: string;
  jihuo_num?: string;
  user_level?: string;
  reward_level?: string;
  donation_level?: string;
  donation_level_text?: string;
  is_activation?: string;
  is_out?: string;
  level_time?: string;
  freeze?: string;
  l?: string;
  r?: string;
  sub_count?: string;
  used_net_weight?: string;
  net_weight?: string;
  activation_code?: string;
  create_time?: string;
  /** PHP relations are stored as serialised JSON in the text column. */
  userLevel?: string;
  rewardLevel?: string;
  recommend?: string;
  parent?: string;
  fuser?: string;
  headimgurl?: string;
  /** Added by backend `/user-mirror-grouped` classifier. */
  account_kind?: 'master' | 'sub' | 'bot' | 'unknown';
  /** How many sub-accounts this master has (0 for non-masters). */
  sub_count_master?: number;
}

interface GroupedRowsResponse {
  total: number;
  page: number;
  page_size: number;
  kind: AccountKind;
  master_account: string | null;
  rows: UserRow[];
  /** Set when `php_user_user` mirror table is missing (no PHP mirror import yet). */
  notice?: string;
}

interface RowDetail { table: string; id: string; _raw: unknown; row: UserRow }

/** GET /api/admin/parity/user-binary-downline (unwrapped `data` from client) */
interface BinaryDownlineData {
  side: 'left' | 'right';
  master_id: string;
  master_account: string;
  leg_root: {
    id: string;
    account: string | null;
    number: string | null;
    nickname: string | null;
  } | null;
  notice?: string;
  /** Full count of the selected leg, including the leg root row shown at depth 0. */
  total_count?: number;
  truncated?: boolean;
  max_depth?: number;
  max_rows?: number;
  rows: Array<{
    id: string;
    account: string | null;
    number: string | null;
    nickname: string | null;
    team_sum: string | null;
    my_sum: string | null;
    freeze?: string | null;
    depth: number;
  }>;
}

/* --------------------------- small helpers ------------------------------ */
function str(v: unknown): string {
  if (v === null || v === undefined) return '';
  return typeof v === 'string' ? v : String(v);
}

function fmtNum(v: unknown, digits = 4): string {
  if (v === null || v === undefined || v === '') return '-';
  const n = typeof v === 'number' ? v : parseFloat(String(v));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString('zh-CN', { maximumFractionDigits: digits });
}

function fmtInt(v: unknown): string {
  if (v === null || v === undefined || v === '') return '-';
  const n = typeof v === 'number' ? v : parseInt(String(v), 10);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString('zh-CN');
}

/** PHP stores relations as JSON-stringified objects inside a text column. */
function parseJsonish(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  const t = v.trim();
  if (!t.startsWith('{') && !t.startsWith('[')) return v;
  try { return JSON.parse(t); } catch { return v; }
}

function readRelationTitle(raw: unknown): string | null {
  const p = parseJsonish(raw);
  if (p && typeof p === 'object' && !Array.isArray(p)) {
    const t = (p as { title?: unknown }).title;
    if (typeof t === 'string' && t) return t;
  }
  return null;
}

function readRelationNumber(raw: unknown): string | null {
  const p = parseJsonish(raw);
  if (p && typeof p === 'object' && !Array.isArray(p)) {
    const n = (p as { number?: unknown }).number;
    if (typeof n === 'string' && n) return n;
    if (typeof n === 'number') return String(n);
  }
  return null;
}

function renderLevelTag(
  value: unknown,
  dict: Record<string, string>,
  relationRaw?: unknown,
  color = 'blue',
): React.ReactNode {
  /** 安置 / 等级在 PHP 里常把整段 JSON 写进 text 列，而不仅是 "2" 这种 id */
  const parsed = parseJsonish(value);
  if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
    const o = parsed as { id?: unknown; title?: string; alias?: string };
    const idKey = o.id !== undefined && o.id !== null ? String(o.id) : '';
    const fromDict = idKey ? dict[idKey] : undefined;
    if (typeof o.title === 'string' && o.title.trim()) {
      const tip = [o.alias?.trim(), fromDict].filter(Boolean).join(' · ') || undefined;
      return (
        <Tag color={color} title={tip}>
          {o.title.trim()}
        </Tag>
      );
    }
    if (fromDict) {
      return (
        <Tag color={color} title={o.alias?.trim() || undefined}>
          {fromDict}
        </Tag>
      );
    }
    if (typeof o.alias === 'string' && o.alias.trim()) {
      return <Tag color={color}>{o.alias.trim()}</Tag>;
    }
  }

  const key = str(value);
  let fromDict = dict[key];
  // 扁平台列为空时，等级 id 只挂在 _raw.userLevel / JSON 的 `id` 上（无 title）— 用 id 走字典
  if (!fromDict && relationRaw !== undefined) {
    const rel = parseJsonish(relationRaw);
    if (rel && typeof rel === 'object' && !Array.isArray(rel) && (rel as { id?: unknown }).id != null) {
      fromDict = dict[String((rel as { id: unknown }).id)];
    }
  }
  if (fromDict) return <Tag color={color}>{fromDict}</Tag>;
  const fromRelation = relationRaw !== undefined ? readRelationTitle(relationRaw) : null;
  if (fromRelation) return <Tag color={color}>{fromRelation}</Tag>;
  const fromValue = readRelationTitle(value);
  if (fromValue) return <Tag color={color}>{fromValue}</Tag>;
  if (!key || key === '0') return <Tag>未分配</Tag>;
  if (key.startsWith('{') || key.startsWith('[')) {
    return <Tag color="default">等级数据异常</Tag>;
  }
  return <Tag color={color}>{key}</Tag>;
}

/**
 * PHP 侧「会员等级」列的语义是 100$ / 1000$ 两档（`selectList` id 1/2, fieldAlias user_level），
 * 但 `_raw.userLevel` 上同时有 `title` 如 E2【二星】。若先渲染 title 会占满整列、看起来像
 * 晋级星阶，和「会员等级」表头及筛选不符 — 故只要 id 能映射到 100$/1000$ 就优先显示档位价。
 * @see php-page-specs.generated.json userLevel.title + selectList 100$ / 1000$
 */
function memberLevelIdFromRow(row: UserRow): string | null {
  const raw = row.user_level;
  const flat = str(raw).trim();
  if (flat && flat !== '0') {
    const p = parseJsonish(raw);
    if (p && typeof p === 'object' && !Array.isArray(p) && (p as { id?: unknown }).id != null) {
      return String((p as { id: unknown }).id);
    }
    if (!flat.startsWith('{') && !flat.startsWith('[')) {
      return flat;
    }
  }
  if (row.userLevel !== undefined && row.userLevel !== null) {
    const rel = parseJsonish(row.userLevel);
    if (rel && typeof rel === 'object' && !Array.isArray(rel) && (rel as { id?: unknown }).id != null) {
      return String((rel as { id: unknown }).id);
    }
  }
  return null;
}

function renderMemberLevelTag(row: UserRow): React.ReactNode {
  const id = memberLevelIdFromRow(row);
  if (id && USER_LEVEL_TITLE[id]) {
    return <Tag color="blue">{USER_LEVEL_TITLE[id]}</Tag>;
  }
  return renderLevelTag(row.user_level, USER_LEVEL_TITLE, row.userLevel);
}

function fmtFreeze(v: unknown): React.ReactNode {
  const raw = str(v).trim();
  const s = raw.toLowerCase();
  if (!s || s === '0' || s === 'false' || s === '0000-00-00 00:00:00') return <Tag>未冻结</Tag>;
  if (s === '1' || s === 'true' || s === 'yes') return <Tag color="red">已冻结</Tag>;
  const d = dayjs(raw);
  const label = d.isValid() ? d.format('YYYY-MM-DD HH:mm') : raw;
  const active = d.isValid() ? d.isAfter(dayjs()) : true;
  return <Tag color={active ? 'red' : 'default'}>{active ? '冻结至 ' : '已解冻 '}{label}</Tag>;
}

function sumEp(row: UserRow): string {
  const a = parseFloat(str(row.ep)) || 0;
  const b = parseFloat(str(row.ep1)) || 0;
  const c = parseFloat(str(row.ep2)) || 0;
  return fmtNum(a + b + c);
}

function amountCol(
  title: string,
  dataIndex: keyof UserRow,
  opts?: { width?: number; int?: boolean; digits?: number },
): ColumnsType<UserRow>[number] {
  const { width = 110, int = false, digits = 2 } = opts ?? {};
  return {
    title,
    dataIndex,
    width,
    align: 'right',
    render: (v: unknown) => (int ? fmtInt(v) : fmtNum(v, digits)),
  };
}

/* ----------------------- freeze / ep / blood forms ---------------------- */
interface ActionModalSpec {
  title: string;
  urlPath: string;
  fields: 'freeze' | 'epLimit' | 'blood' | 'confirm' | 'resetLogin' | 'resetSecret';
  /** Initial form values (pre-populated from the row). */
  initial?: Record<string, unknown>;
}

interface PendingAction extends ActionModalSpec {
  row: UserRow;
}

/* --------------------------- main component ----------------------------- */
export interface PhpUserMgmtPageProps {
  /** 'list' emphasises balance columns; 'operation' emphasises admin state. */
  variant: 'list' | 'operation';
  pageTitle: string;
  pageTag: string;
}

export function PhpUserMgmtPage({ variant, pageTitle, pageTag }: PhpUserMgmtPageProps) {
  const [searchParams] = useSearchParams();
  const initialKind = searchParams.get('kind');
  const initialAccountKind: AccountKind =
    initialKind === 'sub' || initialKind === 'bot' || initialKind === 'all' ? initialKind : 'master';
  const hasNode = useAuthStore((s) => s.hasNode);
  const canShiftUpline = hasNode('platform.user.shift_upline');
  const canDelete = hasNode('platform.user.delete');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [q, setQ] = useState(searchParams.get('q') ?? '');
  const [levelFilter, setLevelFilter] = useState<string | undefined>();
  const [rewardFilter, setRewardFilter] = useState<string | undefined>();
  const [activationFilter, setActivationFilter] = useState<string | undefined>();
  const [isOutFilter, setIsOutFilter] = useState<string | undefined>();
  const [showAdvancedSearch, setShowAdvancedSearch] = useState(false);
  const [advancedSearch, setAdvancedSearch] = useState<AdvancedSearchValues>({});
  const [advancedForm] = Form.useForm<AdvancedSearchValues>();
  /** Default to 主账户 — matches the user's "group by master" request. */
  const [accountKind, setAccountKind] = useState<AccountKind>(initialAccountKind);

  const [detailRow, setDetailRow] = useState<RowDetail | null>(null);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [action, setAction] = useState<PendingAction | null>(null);
  const [shiftTarget, setShiftTarget] = useState<UserRow | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<UserRow | null>(null);

  /** Children cache keyed by master row id. Populated on first expand. */
  const [childrenByMaster, setChildrenByMaster] = useState<Record<string, UserRow[]>>({});
  const [loadingChildren, setLoadingChildren] = useState<Record<string, boolean>>({});
  /** 避免 `loadChildren` 依赖 `childrenByMaster` 导致回调每加载一行就变体、重绑 expandedRow 反复触发请求 */
  const masterExpandLoaded = useRef<Set<string>>(new Set());
  /** 左/右区弹窗：取消上一条请求、忽略过期响应，避免“卡住/叠加载”感 */
  const binaryReqSeq = useRef(0);
  const binaryAbortRef = useRef<AbortController | null>(null);
  /** 网体左/右区下线：安置树子树，仅主账户行可点出 */
  const [binaryModal, setBinaryModal] = useState<{
    side: 'left' | 'right';
    loading: boolean;
    data: BinaryDownlineData | null;
  } | null>(null);

  useEffect(() => {
    const nextQ = searchParams.get('q') ?? '';
    const nextKind = searchParams.get('kind');
    const nextAccountKind: AccountKind =
      nextKind === 'sub' || nextKind === 'bot' || nextKind === 'all' ? nextKind : 'master';
    setQ(nextQ);
    setAccountKind(nextAccountKind);
    setPage(1);
    setChildrenByMaster({});
    masterExpandLoaded.current = new Set();
  }, [searchParams]);

  // Filters are sent as structured params (not mashed into `q`) so the
  // backend can hit indexed flat columns. The backend trigram-search is
  // only used for the free-text `q` box — which stays focused on
  // 账号/编号/昵称/地址.
  const filterKey = [
    q,
    levelFilter,
    rewardFilter,
    activationFilter,
    isOutFilter,
    JSON.stringify(advancedSearch),
  ].join('|');
  const rowsQ = useQuery<GroupedRowsResponse>({
    queryKey: ['php-user-mgmt', accountKind, page, pageSize, filterKey],
    queryFn: () => api.get<GroupedRowsResponse>(GROUPED_URL, {
      kind: accountKind,
      page,
      page_size: pageSize,
      q: q || undefined,
      user_level: levelFilter || advancedSearch.user_level || undefined,
      reward_level: rewardFilter || advancedSearch.reward_level || undefined,
      is_activation: activationFilter || advancedSearch.is_activation || undefined,
      is_out: isOutFilter || advancedSearch.is_out || undefined,
      id: advancedSearch.id || undefined,
      account: advancedSearch.account || undefined,
      number: advancedSearch.number || undefined,
      nickname: advancedSearch.nickname || undefined,
      address: advancedSearch.address || undefined,
      level_time: advancedSearch.level_time || undefined,
      jihuo_num: advancedSearch.jihuo_num || undefined,
      donate: advancedSearch.donate || undefined,
      l: advancedSearch.l || undefined,
      r: advancedSearch.r || undefined,
      sub_count: advancedSearch.sub_count || undefined,
      create_time: advancedSearch.create_time || undefined,
      freeze: advancedSearch.freeze || undefined,
      blood: advancedSearch.blood || undefined,
    }),
    staleTime: 30_000,
    placeholderData: (prev) => prev,
  });

  const refetch = useCallback(() => {
    masterExpandLoaded.current = new Set();
    setChildrenByMaster({});
    return rowsQ.refetch();
  }, [rowsQ]);

  const openDelete = useCallback(
    (row: UserRow) => {
      if (canDelete) {
        setDeleteTarget(row);
        return;
      }
      confirmAndDelete(TABLE, row.id, () => {
        setDetailRow(null);
        refetch();
      });
    },
    [canDelete, refetch],
  );

  const confirmUpgradePackageE2 = useCallback((row: UserRow) => {
    Modal.confirm({
      title: `升级到 1000$ 配套 · ${row.account ?? `#${row.id}`}`,
      content: '仅修正会员配套等级为 1000$，不扣余额、不新增排队单、不补发静态/动态奖励。保留晋级等级（A/S），只写 star_package。出局上限改为 1000×3=3000（不是新开二星的 4 倍）。',
      okText: '确认升级',
      cancelText: '取消',
      async onOk() {
        const data = await api.post<{
          user_id: number;
          old_user_level: string;
          new_user_level: string;
          node_updated: boolean;
        }>('/api/admin/parity/user-package-upgrade-e2', { user_id: row.id });
        message.success(
          `已升级为 1000$ 配套${data.node_updated ? '，Node 用户已同步' : ''}`,
        );
        await refetch();
      },
    });
  }, [refetch]);

  const openBinaryDownline = useCallback(async (row: UserRow, side: 'left' | 'right') => {
    binaryAbortRef.current?.abort();
    const ac = new AbortController();
    binaryAbortRef.current = ac;
    const seq = ++binaryReqSeq.current;
    setBinaryModal({ side, loading: true, data: null });
    try {
      const d = await api.get<BinaryDownlineData>(
        '/api/admin/parity/user-binary-downline',
        { user_id: row.id, side },
        { signal: ac.signal },
      );
      if (seq !== binaryReqSeq.current) return;
      setBinaryModal({ side, loading: false, data: d });
    } catch (e) {
      const name = (e as { name?: string })?.name;
      const code = (e as { code?: string })?.code;
      if (ac.signal.aborted || name === 'AbortError' || name === 'CanceledError' || code === 'ERR_CANCELED') {
        return;
      }
      message.error(e instanceof Error ? e.message : String(e));
      if (seq === binaryReqSeq.current) setBinaryModal(null);
    }
  }, []);

  const loadChildren = useCallback(async (row: UserRow) => {
    const account = typeof row.account === 'string' ? row.account : '';
    if (!account) return;
    if (masterExpandLoaded.current.has(row.id)) return;
    setLoadingChildren((s) => ({ ...s, [row.id]: true }));
    try {
      const res = await api.get<GroupedRowsResponse>(GROUPED_URL, {
        master_account: account,
        page: 1,
        page_size: 200,
      });
      masterExpandLoaded.current.add(row.id);
      setChildrenByMaster((s) => ({ ...s, [row.id]: res.rows }));
    } catch (e) {
      message.error(e instanceof Error ? e.message : String(e));
    } finally {
      setLoadingChildren((s) => ({ ...s, [row.id]: false }));
    }
  }, []);

  /* --------- column definitions (shared identity + per-variant body) --- */

  const idCols: ColumnsType<UserRow> = [
    { title: 'ID', dataIndex: 'id', width: 72, sorter: (a, b) => Number(a.id) - Number(b.id) },
    {
      title: '账号',
      dataIndex: 'account',
      width: 170,
      render: (v, row) => {
        const kindKey = row.account_kind ?? 'unknown';
        const tag = ACCOUNT_KIND_TAG[kindKey] ?? ACCOUNT_KIND_TAG.unknown;
        const sub = Number(row.sub_count_master ?? 0);
        return (
          <Space size={4} wrap>
            <Typography.Text strong={kindKey === 'master'}>{str(v) || '-'}</Typography.Text>
            <Tag color={tag.color}>{tag.label}</Tag>
            {kindKey === 'master' && sub > 0 && (
              <Tooltip title="该主账户的子账号数">
                <Tag color="processing">子 {sub}</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    { title: '编号', dataIndex: 'number', width: 110, render: (v) => str(v) || '-' },
  ];

  const nicknameCol: ColumnsType<UserRow>[number] = {
    title: '昵称',
    dataIndex: 'nickname',
    width: 140,
    ellipsis: true,
    render: (v) => str(v) || '-',
  };

  const recommendPersonCol: ColumnsType<UserRow>[number] = {
    title: '推荐人',
    key: 'recommend',
    width: 100,
    render: (_, row) => readRelationNumber(row.recommend) ?? '-',
  };

  const parentPersonCol: ColumnsType<UserRow>[number] = {
    title: '节点人',
    key: 'parent',
    width: 100,
    render: (_, row) => readRelationNumber(row.parent) ?? '-',
  };

  const fuserPersonCol: ColumnsType<UserRow>[number] = {
    title: '主账户',
    key: 'fuser',
    width: 100,
    render: (_, row) => readRelationNumber(row.fuser) ?? '-',
  };

  const addressCol: ColumnsType<UserRow>[number] = {
    title: '地址',
    dataIndex: 'address',
    width: 220,
    ellipsis: { showTitle: false },
    render: (v: string) => (
      <Tooltip title={v} placement="topLeft">{v || '-'}</Tooltip>
    ),
  };

  /** PHP `user.user/index` — one column per balance / performance field (not collapsed). */
  const phpListAssetCols: ColumnsType<UserRow> = [
    amountCol('源石(uo)', 'uo'),
    amountCol('价值', 'uo_'),
    amountCol('注册积分(rp)', 'rp'),
    amountCol('交易积分(tp)', 'tp'),
    amountCol('现金积分(ep)', 'ep'),
    amountCol('现金积分(ep1)', 'ep1'),
    amountCol('现金积分(ep2)', 'ep2'),
    amountCol('复投积分(sp1)', 'sp1'),
    amountCol('复投积分(sp2)', 'sp2'),
    amountCol('复投积分(sp3)', 'sp3'),
    amountCol('转换积分(mp)', 'mp'),
    amountCol('排队积分(ap)', 'ap'),
    amountCol('拆分积分(fp)', 'fp'),
    amountCol('已获得净量值', 'used_net_weight'),
    amountCol('净量值', 'net_weight', { digits: 0 }),
    amountCol('激活码余额', 'activation_code'),
    amountCol('捐赠币数', 'donate', { digits: 0 }),
    amountCol('团队业绩', 'team_sum', { digits: 0 }),
    amountCol('个人业绩', 'my_sum', { digits: 0 }),
    amountCol('达标1000以上人数', 'jihuo_num', { int: true, width: 130 }),
  ];

  const levelCol: ColumnsType<UserRow>[number] = {
    title: '会员等级',
    key: 'user_level',
    width: 110,
    render: (_, row) => renderMemberLevelTag(row),
  };

  const rewardCol: ColumnsType<UserRow>[number] = {
    title: '晋级等级',
    key: 'reward_level',
    width: 150,
    render: (_, row) => renderLevelTag(row.reward_level, REWARD_LEVEL_TITLE, row.rewardLevel, 'purple'),
  };

  const donationCol: ColumnsType<UserRow>[number] = {
    title: '爱心等级',
    key: 'donation_level',
    width: 150,
    render: (_, row) =>
      renderLevelTag(row.donation_level ?? row.donation_level_text, DONATION_LEVEL_TITLE, undefined, 'magenta'),
  };

  const activationCol: ColumnsType<UserRow>[number] = {
    title: '激活',
    dataIndex: 'is_activation',
    width: 80,
    render: (v: string) => (
      <Tag color={str(v) === '1' ? 'green' : 'default'}>
        {IS_ACTIVATION_TITLE[str(v)] ?? '-'}
      </Tag>
    ),
  };

  const balanceCol: ColumnsType<UserRow>[number] = {
    title: '余额速览',
    key: 'balances',
    width: 220,
    render: (_, row) => {
      const content = (
        <Descriptions size="small" column={1} style={{ minWidth: 260 }}>
          <Descriptions.Item label="源石 ORB">{fmtNum(row.uo)}</Descriptions.Item>
          <Descriptions.Item label="ORB 价值">{fmtNum(row.uo_)}</Descriptions.Item>
          <Descriptions.Item label="EP">{fmtNum(row.ep)}</Descriptions.Item>
          <Descriptions.Item label="EP1">{fmtNum(row.ep1)}</Descriptions.Item>
          <Descriptions.Item label="EP2">{fmtNum(row.ep2)}</Descriptions.Item>
          <Descriptions.Item label="RP">{fmtNum(row.rp)}</Descriptions.Item>
          <Descriptions.Item label="TP">{fmtNum(row.tp)}</Descriptions.Item>
          <Descriptions.Item label="SP1 / SP2 / SP3">
            {fmtNum(row.sp1)} / {fmtNum(row.sp2)} / {fmtNum(row.sp3)}
          </Descriptions.Item>
          <Descriptions.Item label="MP / AP / FP">
            {fmtNum(row.mp)} / {fmtNum(row.ap)} / {fmtNum(row.fp)}
          </Descriptions.Item>
          <Descriptions.Item label="净量值">
            {fmtNum(row.used_net_weight)} / {fmtNum(row.net_weight)}
          </Descriptions.Item>
          <Descriptions.Item label="激活码余额">{fmtNum(row.activation_code)}</Descriptions.Item>
          <Descriptions.Item label="捐赠 ORB">{fmtNum(row.donate)}</Descriptions.Item>
        </Descriptions>
      );
      return (
        <Popover content={content} title="账户余额" trigger="click" placement="right">
          <Space size={4} style={{ cursor: 'pointer' }}>
            <Tag color="gold">ORB {fmtNum(row.uo, 2)}</Tag>
            <Tag color="geekblue">EP {sumEp(row)}</Tag>
          </Space>
        </Popover>
      );
    },
  };

  const bloodCol: ColumnsType<UserRow>[number] = {
    title: '红心数',
    dataIndex: 'blood',
    width: 90,
    align: 'right',
    render: (v) => fmtInt(v),
  };

  const jihuoCol: ColumnsType<UserRow>[number] = {
    title: '直推二星',
    dataIndex: 'jihuo_num',
    width: 110,
    align: 'right',
    render: (v) => fmtInt(v),
  };

  const lrCol: ColumnsType<UserRow>[number] = {
    title: (
      <Tooltip
        title={
          'l、r 为旧 PHP 同步字段；与「主/子账户」及网体双轨「下线左右区」不是同一概念，勿混用。'
        }
      >
        <span>L / R</span>
      </Tooltip>
    ),
    key: 'lr',
    width: 160,
    align: 'right',
    render: (_, row) => (
      <span style={{ fontSize: 12 }}>
        {fmtNum(row.l, 2)} / {fmtNum(row.r, 2)}
      </span>
    ),
  };

  const subCountCol: ColumnsType<UserRow>[number] = {
    title: '子账号',
    dataIndex: 'sub_count',
    width: 90,
    align: 'right',
    render: (v) => fmtInt(v),
  };

  const isOutCol: ColumnsType<UserRow>[number] = {
    title: '出局',
    dataIndex: 'is_out',
    width: 80,
    render: (v: string) => (
      <Tag color={str(v) === '1' ? 'red' : 'default'}>{IS_OUT_TITLE[str(v)] ?? '-'}</Tag>
    ),
  };

  const freezeCol: ColumnsType<UserRow>[number] = {
    title: '冻结状态',
    dataIndex: 'freeze',
    width: 180,
    render: (v) => fmtFreeze(v),
  };

  const createTimeCol: ColumnsType<UserRow>[number] = {
    title: '注册时间',
    dataIndex: 'create_time',
    width: 170,
    render: (v) => str(v) || '-',
  };

  const actionCol: ColumnsType<UserRow>[number] = {
    title: '操作',
    key: 'actions',
    width: 720,
    render: (_, row) => (
      <Space size={4} wrap>
        <Button size="small" type="link" onClick={() => openDetail(row)}>详情</Button>
        <Button size="small" type="link" onClick={() => setEditing(row)}>编辑</Button>
        {canShiftUpline && (
          <Button size="small" type="link" onClick={() => setShiftTarget(row)}>
            平移上级
          </Button>
        )}
        {row.account_kind === 'master' && (
          <>
            <Button size="small" type="link" onClick={() => openBinaryDownline(row, 'left')}>
              左区下线
            </Button>
            <Button size="small" type="link" onClick={() => openBinaryDownline(row, 'right')}>
              右区下线
            </Button>
          </>
        )}
        <Button
          size="small"
          type="link"
          onClick={() => setAction({
            row,
            title: '重置登录密码',
            urlPath: PHP_ACTION.resetLogin,
            fields: 'resetLogin',
          })}
        >
          重置登录密码
        </Button>
        <Button
          size="small"
          type="link"
          onClick={() => confirmInstant(row, '重置交易密码', PHP_ACTION.resetTrade)}
        >
          重置交易密码
        </Button>
        <Button
          size="small"
          type="link"
          onClick={() => setAction({
            row,
            title: '重置密保',
            urlPath: PHP_ACTION.clearSecret,
            fields: 'resetSecret',
          })}
        >
          重置密保
        </Button>
        <Button
          size="small"
          type="link"
          onClick={() => setAction({ row, title: '冻结 / 解冻账户', urlPath: PHP_ACTION.freeze, fields: 'freeze' })}
        >
          冻结 / 解冻
        </Button>
        <Button
          size="small"
          type="link"
          onClick={() => setAction({
            row,
            title: '每日 EP1 / EP2 限额',
            urlPath: PHP_ACTION.epLimit,
            fields: 'epLimit',
            initial: { days_ep_limit: row.days_ep_limit ?? '' },
          })}
        >
          EP 限额
        </Button>
        <Button
          size="small"
          type="link"
          onClick={() => setAction({
            row,
            title: '红心操作',
            urlPath: PHP_ACTION.blood,
            fields: 'blood',
            initial: { blood: row.blood ?? '' },
          })}
        >
          红心操作
        </Button>
        {memberLevelIdFromRow(row) === '1' && (
          <Button size="small" type="link" onClick={() => confirmUpgradePackageE2(row)}>
            升级到1000配套
          </Button>
        )}
        <Button
          size="small"
          type="link"
          danger
          onClick={() => openDelete(row)}
        >
          删除
        </Button>
      </Space>
    ),
  };

  const listCols: ColumnsType<UserRow> = [
    ...idCols,
    recommendPersonCol,
    parentPersonCol,
    fuserPersonCol,
    nicknameCol,
    addressCol,
    bloodCol,
    ...phpListAssetCols,
    levelCol,
    rewardCol,
    donationCol,
    activationCol,
    createTimeCol,
    actionCol,
  ];

  const opCols: ColumnsType<UserRow> = [
    ...idCols,
    nicknameCol,
    addressCol,
    levelCol,
    rewardCol,
    isOutCol,
    freezeCol,
    balanceCol,
    bloodCol,
    jihuoCol,
    lrCol,
    subCountCol,
    createTimeCol,
    actionCol,
  ];

  const columns = variant === 'list' ? listCols : opCols;

  // Fixed left/right columns confuse antd when the table is rendered inside
  // an `expandedRowRender` (it can't measure the outer scroll container).
  // Strip `fixed` and hide the operation column header for the nested
  // children table so it reads as a continuation of the parent row.
  const childColumns: ColumnsType<UserRow> = useMemo(
    () => columns.map((c) => ({ ...c, fixed: undefined })),
    [columns],
  );

  /* ----------------------- action handlers --------------------------- */

  async function openDetail(row: UserRow) {
    try {
      const d = await api.get<RowDetail>(
        `/api/admin/php-mirror/${TABLE}/row/${encodeURIComponent(row.id)}`,
      );
      setDetailRow(d);
    } catch (e) {
      message.error(e instanceof Error ? e.message : String(e));
    }
  }

  function confirmInstant(row: UserRow, title: string, urlPath: string) {
    Modal.confirm({
      title: `${title} · id=${row.id}`,
      content: (
        <div>
          <div>确认对以下用户执行「{title}」？</div>
          <div style={{ marginTop: 6, color: '#888' }}>
            {row.account} · {row.nickname}
          </div>
        </div>
      ),
      okText: '执行',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const r = await api.post<{ php?: { msg?: string } }>(
            `/api/admin/php-mirror/${TABLE}/row/${encodeURIComponent(row.id)}/php-action`,
            { url_path: urlPath, payload: {} },
          );
          message.success(r.php?.msg || '操作成功');
          refetch();
        } catch (e) {
          message.error(e instanceof Error ? e.message : String(e));
        }
      },
    });
  }

  /* ---------------------------- UI ----------------------------------- */

  const applyAdvancedSearch = useCallback((values: AdvancedSearchValues) => {
    const trimmed = Object.fromEntries(
      Object.entries(values)
        .map(([k, v]) => [k, typeof v === 'string' ? v.trim() : v])
        .filter(([, v]) => v !== undefined && v !== ''),
    ) as AdvancedSearchValues;
    setAdvancedSearch(trimmed);
    if (trimmed.user_level !== undefined) setLevelFilter(trimmed.user_level);
    if (trimmed.reward_level !== undefined) setRewardFilter(trimmed.reward_level);
    if (trimmed.is_activation !== undefined) setActivationFilter(trimmed.is_activation);
    if (trimmed.is_out !== undefined) setIsOutFilter(trimmed.is_out);
    setPage(1);
    setChildrenByMaster({});
    masterExpandLoaded.current = new Set();
  }, []);

  const resetAllFilters = useCallback(() => {
    setQ('');
    setLevelFilter(undefined);
    setRewardFilter(undefined);
    setActivationFilter(undefined);
    setIsOutFilter(undefined);
    setAdvancedSearch({});
    advancedForm.resetFields();
    setPage(1);
    setChildrenByMaster({});
    masterExpandLoaded.current = new Set();
  }, [advancedForm]);

  const exportUrl = `/api/admin/php-mirror/${TABLE}/export.csv${
    q ? `?q=${encodeURIComponent(q)}` : ''
  }`;

  return (
    <Card
      size="small"
      title={
        <Space>
          <span>{pageTitle}</span>
          <Tag color="cyan">{pageTag}</Tag>
          <Tag>{TABLE}</Tag>
        </Space>
      }
      extra={
        <Space size="small">
          <Tooltip title="条件搜索">
            <Button
              size="small"
              type={showAdvancedSearch ? 'primary' : 'default'}
              icon={<SearchOutlined />}
              onClick={() => {
                setShowAdvancedSearch((v) => {
                  const next = !v;
                  if (next) {
                    advancedForm.setFieldsValue({
                      ...advancedSearch,
                      user_level: levelFilter,
                      reward_level: rewardFilter,
                      is_activation: activationFilter,
                      is_out: isOutFilter,
                    });
                  }
                  return next;
                });
              }}
            />
          </Tooltip>
          <Tooltip title="重新拉取">
            <Button size="small" type="text" icon={<ReloadOutlined />} onClick={refetch} />
          </Tooltip>
          <Button size="small" icon={<DownloadOutlined />} href={exportUrl} target="_blank">
            导出 CSV
          </Button>
        </Space>
      }
    >
      <Space wrap style={{ marginBottom: 12 }}>
        <Segmented<AccountKind>
          value={accountKind}
          onChange={(v) => {
            setAccountKind(v);
            setPage(1);
            setChildrenByMaster({});
          }}
          options={ACCOUNT_KIND_OPTIONS}
        />
        <Input.Search
          placeholder="搜索 账号 / 编号 / 昵称 / 地址"
          style={{ width: 280 }}
          allowClear
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onSearch={(v) => { setQ(v); setPage(1); }}
        />
        <Tooltip title="与 PHP 一致：仅 100$ / 1000$ 两档（user_level 为 1 或 2），不是下面的星阶。">
          <Select
            placeholder="会员（100$ / 1000$）"
            allowClear
            style={{ minWidth: 180 }}
            value={levelFilter}
            onChange={(v) => { setLevelFilter(v); setPage(1); }}
            options={Object.entries(USER_LEVEL_TITLE).map(([value, label]) => ({ value, label: `${label} (档位 ${value})` }))}
          />
        </Tooltip>
        <Tooltip title="晋级星阶 E1~S9，对应 reward_level；与上栏会员档位是两套值。">
          <Select
            placeholder="晋级等级（E1~S9）"
            allowClear
            style={{ minWidth: 200 }}
            value={rewardFilter}
            onChange={(v) => { setRewardFilter(v); setPage(1); }}
            options={Object.entries(REWARD_LEVEL_TITLE).map(([value, label]) => ({ value, label: `${label} (id ${value})` }))}
          />
        </Tooltip>
        <Select
          placeholder="激活"
          allowClear
          style={{ minWidth: 120 }}
          value={activationFilter}
          onChange={(v) => { setActivationFilter(v); setPage(1); }}
          options={[
            { value: '1', label: '已激活' },
            { value: '0', label: '未激活' },
          ]}
        />
        {variant === 'operation' && (
          <Select
            placeholder="是否出局"
            allowClear
            style={{ minWidth: 140 }}
            value={isOutFilter}
            onChange={(v) => { setIsOutFilter(v); setPage(1); }}
            options={[
              { value: '1', label: '已出局' },
              { value: '0', label: '未出局' },
            ]}
          />
        )}
        {(q || levelFilter || rewardFilter || activationFilter || isOutFilter || Object.keys(advancedSearch).length > 0) && (
          <Button onClick={resetAllFilters}>
            重置
          </Button>
        )}
      </Space>

      {showAdvancedSearch && (
        <Card size="small" title="条件搜索" style={{ marginBottom: 12 }}>
          <Form
            form={advancedForm}
            layout="vertical"
            onFinish={applyAdvancedSearch}
          >
            <Row gutter={[12, 0]}>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="id" label="序号ID">
                  <Input placeholder="请输入序号ID" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="account" label="账号用户名">
                  <Input placeholder="请输入账号用户名" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="number" label="编号ID">
                  <Input placeholder="请输入编号ID" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="nickname" label="昵称">
                  <Input placeholder="请输入昵称" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="address" label="地址">
                  <Input placeholder="请输入地址" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="user_level" label="会员等级">
                  <Select
                    allowClear
                    placeholder="- 全部 -"
                    options={Object.entries(USER_LEVEL_TITLE).map(([value, label]) => ({ value, label }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="level_time" label="原点升级">
                  <Select allowClear placeholder="- 全部 -" options={LEVEL_TIME_OPTIONS} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="is_out" label="是否出局">
                  <Select
                    allowClear
                    placeholder="- 全部 -"
                    options={[
                      { value: '0', label: '否' },
                      { value: '1', label: '是' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="reward_level" label="晋级等级">
                  <Select
                    allowClear
                    placeholder="- 全部 -"
                    options={Object.entries(REWARD_LEVEL_TITLE).map(([value, label]) => ({ value, label }))}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="jihuo_num" label="直推二星数量">
                  <Input placeholder="请输入直推二星数量" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="donate" label="捐赠UO数">
                  <Input placeholder="请输入捐赠UO数" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="l" label="L左区业绩$">
                  <Input placeholder="请输入L左区业绩$" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="r" label="R右区业绩$">
                  <Input placeholder="请输入R右区业绩$" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="sub_count" label="子账号数量">
                  <Select allowClear placeholder="- 全部 -" options={SUB_COUNT_OPTIONS} />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="create_time" label="创建时间">
                  <Input placeholder="请输入创建时间" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="freeze" label="冻结时间至">
                  <Input placeholder="请输入冻结时间至" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="blood" label="红心数量">
                  <Input placeholder="请输入红心数量" allowClear />
                </Form.Item>
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Form.Item name="is_activation" label="激活">
                  <Select
                    allowClear
                    placeholder="- 全部 -"
                    options={[
                      { value: '1', label: '已激活' },
                      { value: '0', label: '未激活' },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Space style={{ width: '100%', justifyContent: 'center' }}>
              <Button type="primary" htmlType="submit">搜索</Button>
              <Button onClick={resetAllFilters}>重置</Button>
            </Space>
          </Form>
        </Card>
      )}

      {rowsQ.data?.notice ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="本页显示的是 PHP 镜像数据，不是 Node 主库会员"
          description={
            <span>
              {rowsQ.data.notice} 标签 <Typography.Text code>parity.user_user</Typography.Text> 表示菜单路由；
              灰色 <Typography.Text code>php_user_user</Typography.Text> 表示实际读取的数据库表。若无需对照旧 PHP
              后台，请改用{' '}
              <Link to="/platform/users">平台 → 用户列表</Link>（表 <Typography.Text code>user</Typography.Text>
              ）。
            </span>
          }
        />
      ) : null}

      <Table<UserRow>
        size="small"
        loading={rowsQ.isFetching}
        scroll={{ x: 'max-content' }}
        dataSource={rowsQ.data?.rows ?? []}
        rowKey={(r) => String(r.id)}
        columns={columns}
        onRow={(row) => ({
          onClick: (e) => {
            // Don't open detail when clicking expand chevron or any button
            // inside the row (操作 column has pointer-events of its own).
            const t = e.target as HTMLElement;
            if (t.closest('button') || t.closest('.ant-table-row-expand-icon')) return;
            openDetail(row);
          },
          style: { cursor: 'pointer' },
        })}
        expandable={{
          rowExpandable: (row) =>
            row.account_kind === 'master' && Number(row.sub_count_master ?? 0) > 0,
          onExpand: (expanded, row) => {
            if (expanded) void loadChildren(row);
          },
          expandedRowRender: (row) => {
            if (loadingChildren[row.id]) {
              return (
                <div style={{ padding: 16, textAlign: 'center' }}>
                  <Spin size="small" /> <span style={{ marginLeft: 8 }}>加载子账户中…</span>
                </div>
              );
            }
            const kids = childrenByMaster[row.id] ?? [];
            if (!kids.length) {
              return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="暂无子账户" />;
            }
            return (
              <Table<UserRow>
                size="small"
                dataSource={kids}
                rowKey={(r) => String(r.id)}
                columns={childColumns}
                pagination={false}
                showHeader={false}
                scroll={{ x: 'max-content' }}
                onRow={(r) => ({
                  onClick: (e) => {
                    const t = e.target as HTMLElement;
                    if (t.closest('button')) return;
                    openDetail(r);
                  },
                  style: { cursor: 'pointer', background: '#fafafa' },
                })}
              />
            );
          },
        }}
        pagination={{
          current: page,
          pageSize,
          total: rowsQ.data?.total ?? 0,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '200'],
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
          showTotal: (t) =>
            `共 ${t} 个${ACCOUNT_KIND_OPTIONS.find((o) => o.value === accountKind)?.label ?? '账户'} · 第 ${page} 页`,
        }}
        locale={{ emptyText: <Empty description={rowsQ.isError ? '加载失败' : '暂无数据'} /> }}
      />

      {/* --- Row detail drawer (read-only, all PHP fields) --- */}
      <UserDetailDrawer
        detail={detailRow}
        onClose={() => setDetailRow(null)}
        onEdit={(row) => { setEditing(row); setDetailRow(null); }}
        onDelete={(row) => openDelete(row)}
      />

      {/* --- Edit drawer (limited mirror fields) --- */}
      <UserEditDrawer
        row={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          refetch();
        }}
      />

      {/* --- Action modal (freeze / ep limit / blood) --- */}
      <UserActionModal
        action={action}
        onClose={() => setAction(null)}
        onDone={() => {
          setAction(null);
          refetch();
        }}
      />

      <Modal
        open={shiftTarget !== null}
        title={`平移上级 — ${shiftTarget?.account ?? shiftTarget?.id ?? ''}`}
        footer={null}
        onCancel={() => setShiftTarget(null)}
        destroyOnClose
        width={560}
      >
        {shiftTarget && (
          <ShiftUplineForm
            userId={Number(shiftTarget.id)}
            username={String(shiftTarget.account ?? shiftTarget.id)}
            onDone={() => {
              setShiftTarget(null);
              refetch();
            }}
          />
        )}
      </Modal>

      <Modal
        open={deleteTarget !== null}
        title={`删除会员 — ${deleteTarget?.account ?? deleteTarget?.id ?? ''}`}
        footer={null}
        onCancel={() => setDeleteTarget(null)}
        destroyOnClose
        width={560}
      >
        {deleteTarget && (
          <DeleteUserForm
            userId={Number(deleteTarget.id)}
            username={String(deleteTarget.account ?? deleteTarget.id)}
            onDone={() => {
              setDeleteTarget(null);
              setDetailRow(null);
              refetch();
            }}
          />
        )}
      </Modal>

      <Modal
        title={
          binaryModal
            ? `${binaryModal.side === 'left' ? '左区下线' : '右区下线'} · ${
                binaryModal.data?.master_account ?? (binaryModal.loading ? '加载中…' : '—')
              }${
                binaryModal.data ? ` · 共 ${fmtInt(binaryModal.data.total_count ?? binaryModal.data.rows.length)} 个` : ''
              }`
            : ''
        }
        open={!!binaryModal}
        onCancel={() => {
          binaryAbortRef.current?.abort();
          setBinaryModal(null);
        }}
        width={880}
        footer={null}
        destroyOnClose
      >
        {binaryModal?.loading ? (
          <div style={{ padding: 40, textAlign: 'center' }}>
            <Spin />
          </div>
        ) : binaryModal?.data ? (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Typography.Paragraph type="secondary" style={{ fontSize: 12, marginBottom: 0 }}>
              以安置 <code>parent</code> 为准：同主号下，按用户 <code>id</code> 升序第 1 个直推为左区根、第 2 个为右区根；下表为该侧区根及其整棵子树（「层」为相对区根深度）。与主/子账号命名（-001）无关。
            </Typography.Paragraph>
            {binaryModal.data.notice && (
              <Typography.Text type="warning">{binaryModal.data.notice}</Typography.Text>
            )}
            {binaryModal.data.truncated && (
              <Typography.Text type="warning" style={{ display: 'block' }}>
                结果已截断（最多返回 {binaryModal.data.max_rows ?? '—'} 行），下方表格不是完整下线树。
              </Typography.Text>
            )}
            <div>
              主账户：
              <Tag>{binaryModal.data.master_account}</Tag>
              {binaryModal.data.leg_root ? (
                <span style={{ marginLeft: 8 }}>
                  区根：
                  <Tag color="blue">{binaryModal.data.leg_root.account}</Tag>
                  <span style={{ color: '#888', marginLeft: 6 }}>id={binaryModal.data.leg_root.id}</span>
                </span>
              ) : (
                <span style={{ marginLeft: 8, color: '#999' }}>该侧无安置直推（无区根）</span>
              )}
            </div>
            <Space size="small" wrap>
              <Tag color={binaryModal.side === 'left' ? 'blue' : 'purple'}>
                {binaryModal.side === 'left' ? '左区' : '右区'}总数：
                {fmtInt(binaryModal.data.total_count ?? binaryModal.data.rows.length)}
              </Tag>
              <Tag>已加载：{fmtInt(binaryModal.data.rows.length)}</Tag>
              <Tag>最大层：{fmtInt(binaryModal.data.max_depth)}</Tag>
            </Space>
            <Table<BinaryDownlineData['rows'][number]>
              size="small"
              rowKey="id"
              dataSource={binaryModal.data.rows}
              pagination={{
                pageSize: 80,
                showSizeChanger: true,
                pageSizeOptions: [40, 80, 120, 200],
                showTotal: (total, range) => (
                  `第 ${range[0]}-${range[1]} 条 / 已加载 ${fmtInt(total)}，本区总数 ${
                    fmtInt(binaryModal.data?.total_count ?? total)
                  }`
                ),
              }}
              scroll={{ y: 420, x: 900 }}
              columns={[
                { title: '层', dataIndex: 'depth', width: 48, fixed: 'left' },
                { title: 'ID', dataIndex: 'id', width: 72, ellipsis: true, fixed: 'left' },
                { title: '账号', dataIndex: 'account', width: 120, ellipsis: true, fixed: 'left' },
                { title: '编号', dataIndex: 'number', width: 88 },
                {
                  title: '账户状态',
                  dataIndex: 'freeze',
                  width: 120,
                  render: (v) => fmtFreeze(v),
                },
                { title: '昵称', dataIndex: 'nickname', width: 110, ellipsis: true },
                {
                  title: '团队业绩',
                  dataIndex: 'team_sum',
                  width: 110,
                  align: 'right',
                  render: (v) => fmtNum(v, 2),
                },
                {
                  title: '个人业绩',
                  dataIndex: 'my_sum',
                  width: 110,
                  align: 'right',
                  render: (v) => fmtNum(v, 2),
                },
              ]}
            />
          </Space>
        ) : null}
      </Modal>
    </Card>
  );
}

/* ============================================================ *
 * Read-only detail drawer
 * ============================================================ */

interface UserDetailDrawerProps {
  detail: RowDetail | null;
  onClose: () => void;
  onEdit: (row: UserRow) => void;
  onDelete: (row: UserRow) => void;
}

function UserDetailDrawer({ detail, onClose, onEdit, onDelete }: UserDetailDrawerProps) {
  const row = detail?.row;
  return (
    <Drawer
      open={!!detail}
      onClose={onClose}
      width={780}
      title={row ? `用户详情 · id=${detail.id}` : '用户详情'}
      extra={
        row && (
          <Space>
            <Button type="primary" onClick={() => onEdit(row)}>编辑</Button>
            <Button danger onClick={() => onDelete(row)}>删除</Button>
          </Space>
        )
      }
    >
      {row && (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <Descriptions size="small" column={2} bordered title="身份">
            <Descriptions.Item label="ID">{str(row.id)}</Descriptions.Item>
            <Descriptions.Item label="账号">{str(row.account) || '-'}</Descriptions.Item>
            <Descriptions.Item label="编号">{str(row.number) || '-'}</Descriptions.Item>
            <Descriptions.Item label="昵称">{str(row.nickname) || '-'}</Descriptions.Item>
            <Descriptions.Item label="地址" span={2}>{str(row.address) || '-'}</Descriptions.Item>
            <Descriptions.Item label="推荐人">{readRelationNumber(row.recommend) ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="节点人">{readRelationNumber(row.parent) ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="主账户">{readRelationNumber(row.fuser) ?? '-'}</Descriptions.Item>
            <Descriptions.Item label="注册时间">{str(row.create_time) || '-'}</Descriptions.Item>
          </Descriptions>

          <Descriptions size="small" column={2} bordered title="等级 / 状态">
            <Descriptions.Item label="会员等级">
              {renderMemberLevelTag(row)}
            </Descriptions.Item>
            <Descriptions.Item label="晋级等级">
              {renderLevelTag(row.reward_level, REWARD_LEVEL_TITLE, row.rewardLevel, 'purple')}
            </Descriptions.Item>
            <Descriptions.Item label="爱心等级">
              {renderLevelTag(row.donation_level ?? row.donation_level_text, DONATION_LEVEL_TITLE, undefined, 'magenta')}
            </Descriptions.Item>
            <Descriptions.Item label="激活">
              <Tag color={str(row.is_activation) === '1' ? 'green' : 'default'}>
                {IS_ACTIVATION_TITLE[str(row.is_activation)] ?? '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="是否出局">
              <Tag color={str(row.is_out) === '1' ? 'red' : 'default'}>
                {IS_OUT_TITLE[str(row.is_out)] ?? '-'}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="冻结状态">{fmtFreeze(row.freeze)}</Descriptions.Item>
          </Descriptions>

          <Descriptions size="small" column={2} bordered title="余额">
            <Descriptions.Item label="源石 ORB">{fmtNum(row.uo)}</Descriptions.Item>
            <Descriptions.Item label="ORB 价值">{fmtNum(row.uo_)}</Descriptions.Item>
            <Descriptions.Item label="EP">{fmtNum(row.ep)}</Descriptions.Item>
            <Descriptions.Item label="EP1">{fmtNum(row.ep1)}</Descriptions.Item>
            <Descriptions.Item label="EP2">{fmtNum(row.ep2)}</Descriptions.Item>
            <Descriptions.Item label="RP">{fmtNum(row.rp)}</Descriptions.Item>
            <Descriptions.Item label="TP">{fmtNum(row.tp)}</Descriptions.Item>
            <Descriptions.Item label="SP1 / SP2 / SP3">
              {fmtNum(row.sp1)} / {fmtNum(row.sp2)} / {fmtNum(row.sp3)}
            </Descriptions.Item>
            <Descriptions.Item label="MP / AP / FP">
              {fmtNum(row.mp)} / {fmtNum(row.ap)} / {fmtNum(row.fp)}
            </Descriptions.Item>
            <Descriptions.Item label="净量值 (已用 / 总)">
              {fmtNum(row.used_net_weight)} / {fmtNum(row.net_weight)}
            </Descriptions.Item>
            <Descriptions.Item label="激活码余额">{fmtNum(row.activation_code)}</Descriptions.Item>
            <Descriptions.Item label="捐赠 ORB">{fmtNum(row.donate)}</Descriptions.Item>
            <Descriptions.Item label="红心">{fmtInt(row.blood)}</Descriptions.Item>
          </Descriptions>

          <Descriptions size="small" column={2} bordered title="业绩">
            <Descriptions.Item label="说明" span={2}>
              <Typography.Text type="secondary" style={{ fontSize: 12, lineHeight: 1.6 }}>
                字段 l、r 为旧 PHP 同步值。其与「主账户 / 子账户」账号体系（如主号与
                -001、-002）是不同维度，也<strong>不是</strong>网体里俗称的「下线双轨左右区」；同一主号下多子户仅表示账号拆分，不由此推断
                l/r 的含义。记账上常仍见 team_sum ≈ l + r + 个人业绩。
              </Typography.Text>
            </Descriptions.Item>
            <Descriptions.Item label="个人业绩">{fmtNum(row.my_sum, 2)}</Descriptions.Item>
            <Descriptions.Item label="团队业绩">{fmtNum(row.team_sum, 2)}</Descriptions.Item>
            <Descriptions.Item label="直推二星">{fmtInt(row.jihuo_num)}</Descriptions.Item>
            <Descriptions.Item label="子账号数量">{fmtInt(row.sub_count)}</Descriptions.Item>
            <Descriptions.Item label="L">{fmtNum(row.l, 2)}</Descriptions.Item>
            <Descriptions.Item label="R">{fmtNum(row.r, 2)}</Descriptions.Item>
          </Descriptions>

          <Card size="small" title="原始 JSON (_raw)">
            <pre style={{ whiteSpace: 'pre-wrap', maxHeight: 320, overflow: 'auto', margin: 0 }}>
              {JSON.stringify(detail._raw, null, 2)}
            </pre>
          </Card>
        </Space>
      )}
    </Drawer>
  );
}

/* ============================================================ *
 * Edit drawer — only the fields the legacy PHP admin actually
 * allowed operators to edit via the mirror (nickname / blood /
 * is_activation). Business-critical balances are intentionally
 * NOT editable here — use 余额调整 on /platform/users for those.
 * ============================================================ */

interface UserEditDrawerProps {
  row: UserRow | null;
  onClose: () => void;
  onSaved: () => void;
}

function UserEditDrawer({ row, onClose, onSaved }: UserEditDrawerProps) {
  const [form] = Form.useForm<{ nickname: string; blood: string; is_activation: string }>();
  const [saving, setSaving] = useState(false);

  async function submit() {
    if (!row) return;
    try {
      const values = await form.validateFields();
      setSaving(true);
      await api.patch(`/api/admin/php-mirror/${TABLE}/row/${encodeURIComponent(row.id)}`, {
        fields: {
          nickname: values.nickname ?? '',
          blood: values.blood ?? '',
          is_activation: values.is_activation ?? '0',
        },
      });
      message.success('已保存');
      onSaved();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Drawer
      open={!!row}
      onClose={onClose}
      width={520}
      title={row ? `编辑用户 · ${row.account ?? ''} (#${row.id})` : '编辑用户'}
      destroyOnClose
      footer={
        <Space style={{ float: 'right' }}>
          <Button onClick={onClose}>取消</Button>
          <Button type="primary" loading={saving} onClick={submit}>保存</Button>
        </Space>
      }
    >
      {row && (
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            nickname: str(row.nickname),
            blood: str(row.blood),
            is_activation: str(row.is_activation) || '0',
          }}
        >
          <Form.Item label="账号" tooltip="由系统生成，不可修改">
            <Input value={str(row.account)} disabled />
          </Form.Item>
          <Form.Item label="编号" tooltip="由系统生成，不可修改">
            <Input value={str(row.number)} disabled />
          </Form.Item>
          <Form.Item name="nickname" label="昵称">
            <Input maxLength={64} />
          </Form.Item>
          <Form.Item name="blood" label="红心" tooltip="如需触发 PHP 的日志/扣减逻辑，请用「红心操作」按钮。">
            <Input />
          </Form.Item>
          <Form.Item name="is_activation" label="激活">
            <Select
              options={[
                { value: '0', label: '未激活' },
                { value: '1', label: '已激活' },
              ]}
            />
          </Form.Item>
          <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
            余额字段（ORB / EP / SP / …）不在此修改。请使用「调整余额」页面（platform.user.adjust），
            或通过 PHP 镜像表 (system.php_mirror) 做原始写入。
          </Typography.Paragraph>
        </Form>
      )}
    </Drawer>
  );
}

/* ============================================================ *
 * Action modal — freeze / ep limit / blood; fires /php-action
 * ============================================================ */

interface UserActionModalProps {
  action: PendingAction | null;
  onClose: () => void;
  onDone: () => void;
}

function UserActionModal({ action, onClose, onDone }: UserActionModalProps) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);

  async function submit() {
    if (!action) return;
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      // Coerce InputNumber → string (the PHP backend speaks form-urlencoded).
      const payload: Record<string, unknown> = {};
      for (const [k, v] of Object.entries(values)) {
        payload[k] = v === null || v === undefined ? '' : String(v);
      }
      const r = await api.post<{ php?: { msg?: string } }>(
        `/api/admin/php-mirror/${TABLE}/row/${encodeURIComponent(action.row.id)}/php-action`,
        { url_path: action.urlPath, payload },
      );
      message.success(r.php?.msg || '操作成功');
      form.resetFields();
      onDone();
    } catch (e) {
      if (e instanceof Error) message.error(e.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={!!action}
      title={action ? `${action.title} · ${action.row.account ?? ''} (#${action.row.id})` : ''}
      onCancel={() => { form.resetFields(); onClose(); }}
      onOk={submit}
      confirmLoading={submitting}
      destroyOnClose
      okText="执行"
    >
      {action && (
        <Form form={form} layout="vertical" initialValues={action.initial ?? {}}>
          {action.fields === 'freeze' && (
            <>
              <Form.Item
                name="freeze"
                label="冻结操作"
                rules={[{ required: true, message: '请选择冻结时长或解冻' }]}
                extra="选择冻结时长（自今日起算），或选择“解冻”立即解除冻结。PHP 只接受这些枚举值。"
              >
                <Select
                  options={[
                    { value: '+3', label: '冻结账户 3 天' },
                    { value: '+7', label: '冻结账户 7 天' },
                    { value: '+30', label: '冻结账户 30 天' },
                    { value: '+90', label: '冻结账户 90 天' },
                    { value: '+36500', label: '永久冻结账户' },
                    { value: '-1', label: '解冻' },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="freeze_remark"
                label="冻结原因备注"
                rules={[{ required: true, message: '请填写冻结/解冻原因' }]}
              >
                <Input.TextArea rows={3} placeholder="必填，将写入 PHP 日志" />
              </Form.Item>
            </>
          )}
          {action.fields === 'epLimit' && (
            <Form.Item
              name="days_ep_limit"
              label="每日 EP1 / EP2 限额"
              rules={[{ required: true, message: '请填写限额数量' }]}
              extra="填 0 表示不限额。"
            >
              <InputNumber min={0} style={{ width: '100%' }} />
            </Form.Item>
          )}
          {action.fields === 'blood' && (
            <Form.Item
              name="blood"
              label="红心数量"
              rules={[{ required: true, message: '请填写红心数量' }]}
            >
              <InputNumber style={{ width: '100%' }} />
            </Form.Item>
          )}
          {action.fields === 'resetLogin' && (
            <Form.Item
              name="password"
              label="新登录密码"
              rules={[
                { required: true, message: '请输入新登录密码' },
                { min: 4, message: '密码至少 4 位' },
              ]}
              extra="手动输入后写入平台登录哈希。勿设为 123456（默认弱密码会被禁止登录）。"
            >
              <Input.Password placeholder="手动输入新密码" autoComplete="new-password" />
            </Form.Item>
          )}
          {action.fields === 'resetSecret' && (
            <Form.Item
              name="security_answer"
              label="新密保答案"
              rules={[
                { required: true, message: '请输入新密保答案' },
                { min: 1, message: '密保不能为空' },
              ]}
              extra="手动输入后写入密保哈希。勿设为 123456（默认弱密保会被禁止登录）。"
            >
              <Input placeholder="手动输入密保答案" autoComplete="off" />
            </Form.Item>
          )}
        </Form>
      )}
    </Modal>
  );
}

/* ============================================================ *
 * Thin wrappers for the two parity routes
 * ============================================================ */

export function PhpUserListParityPage() {
  return <PhpUserMgmtPage variant="list" pageTitle="用户列表" pageTag="parity.user_user" />;
}

export function PhpUserOperationParityPage() {
  return <PhpUserMgmtPage variant="operation" pageTitle="用户操作" pageTag="parity.user_useroperation" />;
}
